import { useCallback, useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import AdminPageHeader from "@/pages/admin/components/AdminPageHeader";
import { useAuth } from "@/context/AuthContext";
import {
  getAdminPopupAds,
  saveAdminPopupAd,
  deleteAdminPopupAd,
  type PopupAdInput,
} from "@/services/adminApi";
import type { PopupAd } from "@/types";
import PopupAdCard from "@/pages/admin/popup-ads/components/PopupAdCard";
import PopupAdEditor from "@/pages/admin/popup-ads/components/PopupAdEditor";
import { popupAdStatus } from "@/pages/admin/popup-ads/utils";

/** Builds a full PopupAd row from a save payload (id/new handled by caller). */
function toRow(input: PopupAdInput, id: string, impressions = 0): PopupAd {
  return {
    id,
    title: input.title,
    mediaType: input.mediaType,
    mediaUrl: input.mediaUrl,
    linkUrl: input.linkUrl || "",
    linkLabel: input.linkLabel || "",
    targetPages: input.targetPages,
    frequencyCap: input.frequencyCap ?? 0,
    delaySeconds: input.delaySeconds ?? 0,
    impressions,
    startAt: input.startAt || "",
    endAt: input.endAt || "",
    isActive: input.isActive ?? true,
    priority: input.priority ?? 0,
  };
}

export default function AdminPopupAds() {
  const { token } = useAuth();
  const [rows, setRows] = useState<PopupAd[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState<PopupAd | null>(null);
  const [showEditor, setShowEditor] = useState(false);

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2400);
  };

  const load = useCallback(() => {
    setLoading(true);
    setError("");
    getAdminPopupAds(token)
      .then((list) => {
        setRows(list.map((ad) => ({ ...ad, targetPages: [...ad.targetPages] })));
      })
      .catch((err: unknown) => {
        setRows([]);
        setError(err instanceof Error ? err.message : "Could not load the popups. Please try again.");
      })
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    load();
  }, [load]);

  const stats = useMemo(() => {
    const active = rows.filter((ad) => popupAdStatus(ad).tone === "active").length;
    const impressions = rows.reduce((sum, ad) => sum + ad.impressions, 0);
    return { total: rows.length, active, impressions };
  }, [rows]);

  const openNew = () => {
    setEditing(null);
    setShowEditor(true);
    setError("");
  };

  const openEdit = (ad: PopupAd) => {
    setEditing(ad);
    setShowEditor(true);
    setError("");
  };

  const closeEditor = () => {
    setShowEditor(false);
    setEditing(null);
  };

  const save = async (input: PopupAdInput) => {
    setSaving(true);
    setError("");
    try {
      await saveAdminPopupAd(input, token);
      if (input.id) {
        setRows((prev) => prev.map((r) => (r.id === input.id ? toRow(input, r.id, r.impressions) : r)));
      } else {
        setRows((prev) => [toRow(input, `local-${Date.now()}`), ...prev]);
      }
      flash(input.id ? "Popup saved." : "Popup created.");
      closeEditor();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not save the popup. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const toggle = async (ad: PopupAd) => {
    const next = !ad.isActive;
    setRows((prev) => prev.map((r) => (r.id === ad.id ? { ...r, isActive: next } : r)));
    try {
      await saveAdminPopupAd(
        {
          id: ad.id,
          title: ad.title,
          mediaType: ad.mediaType,
          mediaUrl: ad.mediaUrl,
          linkUrl: ad.linkUrl,
          linkLabel: ad.linkLabel,
          targetPages: ad.targetPages,
          frequencyCap: ad.frequencyCap,
          delaySeconds: ad.delaySeconds,
          startAt: ad.startAt,
          endAt: ad.endAt,
          isActive: next,
          priority: ad.priority,
        },
        token,
      );
      flash(next ? "Popup enabled." : "Popup disabled.");
    } catch {
      setRows((prev) => prev.map((r) => (r.id === ad.id ? { ...r, isActive: ad.isActive } : r)));
      setError("Could not update the popup. Please try again.");
    }
  };

  const remove = async (ad: PopupAd) => {
    try {
      const okDelete = await deleteAdminPopupAd(ad.id, token);
      if (!okDelete) throw new Error("failed");
    } catch {
      setError("Could not remove the popup. Please try again.");
      return;
    }
    setRows((prev) => prev.filter((r) => r.id !== ad.id));
    if (editing?.id === ad.id) closeEditor();
    flash("Popup removed.");
  };

  return (
    <>
      <Seo title="Popup Ads" path="/admin/popup-ads" />

      <AdminPageHeader
        icon="ri-window-2-line"
        title="Popup ads"
        subtitle="Centre-screen lightbox popups — an image or short video with page targeting, a frequency cap and an end date."
        actions={
          <>
            {notice && (
              <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
                <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
              </span>
            )}
            <button
              type="button"
              onClick={openNew}
              className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i> New popup
            </button>
          </>
        }
      />

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Popups</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">{stats.total}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Live now</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-accent-700">{stats.active}</p>
        </div>
        <div className="rounded-lg border border-background-200 bg-background-50 p-4">
          <span className="text-xs font-semibold uppercase tracking-wide text-foreground-500">Total impressions</span>
          <p className="mt-2 font-heading text-2xl font-extrabold text-foreground-950">
            {stats.impressions.toLocaleString()}
          </p>
        </div>
      </div>

      {error && (
        <div className="mt-4 flex items-start gap-3 rounded-md border border-secondary-200 bg-secondary-50 p-3.5 text-sm text-secondary-900">
          <i className="ri-error-warning-line mt-0.5 text-lg text-secondary-600"></i>
          <div className="flex-1">
            <p>{error}</p>
            {rows.length === 0 && (
              <button
                type="button"
                onClick={load}
                className="mt-2 inline-flex items-center gap-2 rounded-md border border-secondary-300 px-3 py-1.5 text-xs font-semibold text-secondary-900 hover:bg-secondary-100 whitespace-nowrap cursor-pointer"
              >
                <i className="ri-refresh-line"></i> Try again
              </button>
            )}
          </div>
        </div>
      )}

      {showEditor && (
        <div className="mt-6">
          <PopupAdEditor
            initial={editing}
            token={token}
            saving={saving}
            onSave={save}
            onCancel={closeEditor}
          />
        </div>
      )}

      <div className="mt-6 space-y-3">
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="h-32 animate-pulse rounded-lg border border-background-200 bg-background-100/60"></div>
          ))
        ) : rows.length === 0 ? (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 p-10 text-center">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-primary-700">
              <i className="ri-window-2-line text-xl"></i>
            </span>
            <p className="mt-3 text-sm font-semibold text-foreground-800">No popup ads yet</p>
            <p className="mt-1 text-sm text-foreground-500">
              Create your first popup to greet visitors with an image or a short video.
            </p>
            <button
              type="button"
              onClick={openNew}
              className="mt-4 inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-bold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-add-line"></i> New popup
            </button>
          </div>
        ) : (
          rows.map((ad) => (
            <PopupAdCard key={ad.id} ad={ad} onEdit={openEdit} onDelete={remove} onToggle={toggle} />
          ))
        )}
      </div>
    </>
  );
}